import { faClone, faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import { CampaignDisplayStatus, type CampaignStat } from "@signalist/shared-types";

import type { ReactNode } from "react";

import { CampaignValidationState } from "@/pages/Private/pages/List/components/CampaignOverview/utils/useCampaignValidation";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
// Plain builder, not a hook — the standalone i18n instance is how non-React config is translated
// here (see constants/sidenav.config.tsx). Keeps the caller (ListEditCopywriting) untouched.
import i18n from "@/i18n";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { CampaignStatusPill } from "../../../components/CampaignOverview/components/CampaignStatusPill";
import { CampaignLockIcon } from "../../../components/CampaignOverview/components/CampaignStatusBadge";
import { SenderAvatars } from "../../../components/CampaignOverview/components/SenderAvatars";

import { CampaignOutboxLinks } from "./CampaignOutboxLinks";

import type { TableColumn } from "@/components/Table/Table";

import type { CopywritingCampaign } from "../ListEditCopywriting";

/** Positive-reply rate color, matching the mockup thresholds. */
export const replyColorClass = (pct: number): string =>
	pct >= 15 ? "text-status-success" : pct >= 10 ? "text-blue-action" : "text-text-meta";

/** "12 May 2026" for the event Stops-at column. */
export const formatStopDate = (value?: Date | string | null): string =>
	value
		? new Date(value).toLocaleDateString("en-GB", {
				day: "2-digit",
				month: "short",
				year: "numeric",
			})
		: "";

/** Per-campaign stats returned by the campaign-stats endpoint. */
export type CampaignStats = Omit<CampaignStat, "campaignId">;

/** Minimal slice of the list-query data used by the columns. */
interface CampaignColumnData {
	/** The list the table belongs to; scopes the Outbox links (PREC-4291). */
	id?: number;
	name?: string | null;
	campaignStatuses?: Record<
		string,
		{ status?: CampaignDisplayStatus; locked?: boolean; invalidatedAt?: string | null }
	> | null;
	event?: { enabled?: boolean } | null;
}

export interface BuildCampaignColumnsParams {
	data?: CampaignColumnData;
	/** Live per-campaign validity + reasons (PREC-3952), keyed by campaign uuid. */
	campaignValidation?: Record<string, CampaignValidationState>;
	statsByCampaign: Record<string, CampaignStats>;
	/** Whether the stats endpoint has answered; until then a missing stat is not a zero. */
	statsLoaded?: boolean;
	isTogglingCampaignStatus: boolean;
	onToggleCampaignRunStatus: (campaignUuid: string, status: CampaignDisplayStatus) => void;
	onSetAsDefault: (campaign: CopywritingCampaign) => void;
	onCloneCampaign: (campaign: CopywritingCampaign) => void;
	onDeleteCampaign: (campaign: CopywritingCampaign) => void;
}

const COLUMN_COMMON = {
	allowExclude: false,
	hideable: false,
	moveable: false,
} as const;

/**
 * Builds the campaign table columns for the copywriting page.
 *
 * The "Positive Reply" column shows both the actual count and the percentage
 * (e.g. `3 (15%)`), mirroring the "Ended" column format while preserving
 * the threshold-based colouring.
 */
/**
 * The lines under an INVALID badge (PREC-3952, PREC-4153). A stale flag has no rules to report —
 * the campaign only has to be opened once — so that case gets the instruction instead of an empty
 * tooltip.
 *
 * `invalidatedAt` leads, because a badge with no date reads as if the campaign has just broken:
 * PREC-4153's was destroyed on 28 August, ran from 11 September and was only marked invalid on the
 * 14th, when somebody opened it. The rules below it are computed in the browser and describe the
 * campaign as it is now; the date comes from the server and says since when.
 */
export const campaignInvalidReasons = (
	status: CampaignDisplayStatus,
	// Only the *why* — `isValid` is the verdict and is decided by the caller, which passes a full
	// CampaignValidationState and satisfies this narrower shape.
	validation?: Pick<CampaignValidationState, "errors" | "needsRefresh">,
	invalidatedAt?: string | null
): string[] | undefined => {
	if (status !== CampaignDisplayStatus.INVALID) {
		return undefined;
	}

	const rules = validation?.needsRefresh
		? [i18n.t("list.reachOut.picker.needsRefresh")]
		: (validation?.errors.map((error) => error.message) ?? []);

	if (!invalidatedAt) {
		return rules;
	}

	const since = i18n.t("list.reachOut.picker.invalidSince", {
		date: new Date(invalidatedAt).toLocaleDateString(),
	});

	return [since, ...rules];
};

export const buildCampaignColumns = (params: BuildCampaignColumnsParams): TableColumn[] => {
	const { data, statsByCampaign, isTogglingCampaignStatus } = params;

	return [
		{
			title: "Status",
			uid: "status",
			...COLUMN_COMMON,
			render: (row: CopywritingCampaign) => {
				const info = data?.campaignStatuses?.[row.id];
				const status =
					info?.status ??
					(row.isValid ? CampaignDisplayStatus.VALID : CampaignDisplayStatus.INVALID);

				return (
					<div onClick={(e) => e.stopPropagation()}>
						<CampaignStatusPill
							disabled={isTogglingCampaignStatus}
							reasons={campaignInvalidReasons(
								status,
								params.campaignValidation?.[row.id],
								info?.invalidatedAt
							)}
							status={status}
							onToggle={() => params.onToggleCampaignRunStatus(row.id, status)}
						/>
					</div>
				);
			},
		},
		{
			title: "Campaign Name",
			uid: "name",
			...COLUMN_COMMON,
			render: (row: CopywritingCampaign) => {
				const info = data?.campaignStatuses?.[row.id];

				return (
					<div className="flex items-center gap-2">
						<span className="font-medium">
							{row.name} {row.isDefault ? "(Default)" : ""}
						</span>
						{info?.locked && <CampaignLockIcon />}
					</div>
				);
			},
		},
		{
			title: "Senders",
			uid: "senders",
			...COLUMN_COMMON,
			render: (row: CopywritingCampaign) => <SenderAvatars senders={row.senders} />,
		},
		{
			title: "Total Leads",
			uid: "totalLeads",
			align: "right",
			...COLUMN_COMMON,
			// The links go before the count, so the count ends the right-aligned cell and lines up with
			// the footer total. No links once the stats say the campaign has no lead at all (none
			// assigned, none contacted): there is nothing to open. While they are loading, or failed,
			// the links stay; a missing number is not a zero.
			render: (row: CopywritingCampaign) => {
				const stat = statsByCampaign[row.id];
				const totalLeads = stat?.totalLeads ?? 0;
				const hasNoLeads = !!params.statsLoaded && totalLeads === 0 && !stat?.contacted;

				return (
					<span className="inline-flex items-center justify-end gap-3">
						{data?.id !== undefined && !hasNoLeads && (
							<CampaignOutboxLinks campaignUuid={row.id} listId={data.id} listName={data.name} />
						)}
						<span className="tabular-nums">{totalLeads.toLocaleString("en-US")}</span>
					</span>
				);
			},
		},
		{
			// PREC-4291: "Started" in the Turing call. The leads a message reached, the same count as
			// "Contacted" in the Analytics funnel. A count without a share: the API counts a contacted
			// lead in any status, Total Leads the assigned statuses only. The hint sits on the value,
			// as on Ended below.
			title: i18n.t("list.campaignRollup.contactedColumn"),
			uid: "contacted",
			align: "right",
			...COLUMN_COMMON,
			render: (row: CopywritingCampaign) => (
				<Tooltip content={i18n.t("list.campaignRollup.contactedColumnHint")} direction="top">
					<span className="tabular-nums">
						{(statsByCampaign[row.id]?.contacted ?? 0).toLocaleString("en-US")}
					</span>
				</Tooltip>
			),
		},
		{
			// "Ended" since PREC-4291 (was "Completed"): the Analytics name for the same count.
			title: i18n.t("list.campaignRollup.completedColumn"),
			uid: "completed",
			align: "right",
			...COLUMN_COMMON,
			// "Completed" spans four assign statuses (ENDED, MESSAGE_SENT, REPLIES, DONE), so the bare
			// number reads as "leads marked done" and looks far too low — PREC-3811 was reported
			// precisely because the label hid what it counted. The hint sits on the value rather than
			// the header because Table only renders a column's `iconRender` for sortable/clickable
			// headers (Table.tsx), and this column is neither.
			render: (row: CopywritingCampaign) => {
				const stat = statsByCampaign[row.id];
				const total = stat?.totalLeads ?? 0;
				const completed = stat?.completed ?? 0;

				return (
					<Tooltip content={i18n.t("list.campaignRollup.completedColumnHint")} direction="top">
						<span className="tabular-nums">
							{completed.toLocaleString("en-US")}
							{completed > 0 && total > 0 ? (
								<span className="text-text-meta"> ({Math.round((completed / total) * 100)}%)</span>
							) : null}
						</span>
					</Tooltip>
				);
			},
		},
		{
			title: "Positive Reply",
			uid: "positiveReply",
			align: "right",
			...COLUMN_COMMON,
			render: (row: CopywritingCampaign) => {
				const stat = statsByCampaign[row.id];
				const total = stat?.totalLeads ?? 0;
				const positiveReplies = stat?.positiveReplies ?? 0;

				if (total === 0) {
					return <span className="text-text-meta">—</span>;
				}

				const pct = Math.round((positiveReplies / total) * 100);

				return (
					<span className="tabular-nums">
						{positiveReplies}
						{positiveReplies > 0 && total > 0 ? (
							<span className={classNames("font-semibold", replyColorClass(pct))}> ({pct}%)</span>
						) : null}
					</span>
				);
			},
		},
		...(data?.event?.enabled
			? [
					{
						title: "Stops at",
						uid: "stopsAt",
						...COLUMN_COMMON,
						render: (row: CopywritingCampaign) =>
							row.stopCampaignAt ? (
								<span className="whitespace-nowrap text-sm text-text-meta">
									{formatStopDate(row.stopCampaignAt)}
								</span>
							) : (
								<span className="whitespace-nowrap text-sm text-status-warning">Not set</span>
							),
					},
				]
			: []),
		{
			title: "Actions",
			uid: "actions",
			...COLUMN_COMMON,
			render: (row: CopywritingCampaign) => (
				<div className="flex items-center gap-2">
					{!row.isDefault && (
						<Tooltip direction="top" content={i18n.t("list.campaignRollup.makeDefaultHint")}>
							<div className="w-fit">
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									title="Make default campaign"
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
										params.onSetAsDefault(row);
									}}
								/>
							</div>
						</Tooltip>
					)}
					<button
						className="text-text-meta hover:cursor-pointer hover:text-brand-light"
						title="Duplicate campaign"
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							params.onCloneCampaign(row);
						}}
					>
						<Icon icon={faClone} size="lg" />
					</button>
					<button
						className="text-text-meta hover:cursor-pointer hover:text-status-error"
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							params.onDeleteCampaign(row);
						}}
					>
						<Icon icon={faTrashCan} size="lg" />
					</button>
				</div>
			),
		},
	];
};

export interface CampaignSummary {
	count: number;
	active: number;
	totalLeads: number;
	totalContacted: number;
	totalCompleted: number;
	completedPct: number;
	totalPositive: number;
	avgReply: number;
}

/** Computes the aggregate totals for the summary footer row. */
export const computeSummary = (
	tableData: CopywritingCampaign[],
	statsByCampaign: Record<string, CampaignStats>,
	campaignStatuses?: Record<string, { status?: CampaignDisplayStatus }> | null
): CampaignSummary => {
	let totalLeads = 0;
	let totalContacted = 0;
	let totalCompleted = 0;
	let totalPositive = 0;
	let active = 0;

	tableData.forEach((row) => {
		const stat = statsByCampaign[row.id];

		if (stat) {
			totalLeads += stat.totalLeads;
			totalContacted += stat.contacted ?? 0;
			totalCompleted += stat.completed;
			totalPositive += stat.positiveReplies;
		}

		if (campaignStatuses?.[row.id]?.status === CampaignDisplayStatus.ACTIVE) {
			active += 1;
		}
	});

	return {
		count: tableData.length,
		active,
		totalLeads,
		totalContacted,
		totalCompleted,
		completedPct: totalLeads > 0 ? Math.round((totalCompleted / totalLeads) * 100) : 0,
		totalPositive,
		avgReply: totalLeads > 0 ? Math.round((totalPositive / totalLeads) * 100) : 0,
	};
};

/**
 * Builds the `<tfoot>` cell contents for the campaign table, one entry per
 * column (indexed by column position).  Numeric totals are placed under their
 * matching columns so they align perfectly with the table layout.
 */
export const buildFooterRow = (columns: TableColumn[], summary: CampaignSummary): ReactNode[] => {
	return columns.map((col) => {
		switch (col.uid) {
			case "name":
				return (
					<span key={col.uid} className="font-medium text-text-meta">
						{summary.count} campaign{summary.count !== 1 ? "s" : ""} · {summary.active} active
					</span>
				);
			case "totalLeads":
				return (
					<span key={col.uid} className="font-semibold tabular-nums">
						{summary.totalLeads.toLocaleString("en-US")}
					</span>
				);
			case "contacted":
				return (
					<span key={col.uid} className="font-semibold tabular-nums">
						{summary.totalContacted.toLocaleString("en-US")}
					</span>
				);
			case "completed":
				return (
					<span key={col.uid} className="font-semibold tabular-nums">
						{summary.totalCompleted.toLocaleString("en-US")}
						{summary.totalCompleted > 0 && summary.totalLeads > 0 ? (
							<span className="font-normal text-text-meta"> ({summary.completedPct}%)</span>
						) : null}
					</span>
				);
			case "positiveReply":
				if (summary.totalLeads === 0) {
					return (
						<span key={col.uid} className="text-text-meta">
							—
						</span>
					);
				}

				return (
					<span key={col.uid} className="font-semibold tabular-nums">
						{summary.totalPositive.toLocaleString("en-US")}
						{summary.totalPositive > 0 ? (
							<span className={replyColorClass(summary.avgReply)}> ({summary.avgReply}%)</span>
						) : null}
					</span>
				);
			default:
				return null;
		}
	});
};
